/**
 * Session-expiry bus. The fetch wrappers in services/ call
 * {@link handleUnauthorizedResponse} on every 401; the AuthProvider listens
 * with {@link onUnauthorized} and sends the operator to /login (preserving the
 * return path). Kept dependency-free so services/ and auth/ do not import
 * each other's React trees.
 */
import { clearSession, getSessionToken } from './session';

export const UNAUTHORIZED_EVENT = 'enso:auth:unauthorized';

export interface UnauthorizedDetail {
  status: number;
  /** Request path that failed (for diagnostics only; never the token). */
  path?: string;
  /** Whether a session token was present when the 401 arrived. */
  hadSession: boolean;
}

export function emitUnauthorized(detail: UnauthorizedDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent<UnauthorizedDetail>(UNAUTHORIZED_EVENT, { detail }));
}

export function onUnauthorized(handler: (detail: UnauthorizedDetail) => void): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const listener = (event: Event) => {
    handler((event as CustomEvent<UnauthorizedDetail>).detail);
  };
  window.addEventListener(UNAUTHORIZED_EVENT, listener);
  return () => window.removeEventListener(UNAUTHORIZED_EVENT, listener);
}

/**
 * Central 401 handling for every API wrapper: clears the stored session (so
 * the next request cannot reuse the dead token) and notifies the AuthProvider.
 * Returns true when the status was a 401.
 */
export function handleUnauthorizedResponse(status: number, path?: string): boolean {
  if (status !== 401) return false;
  const hadSession = Boolean(getSessionToken());
  if (hadSession) clearSession();
  emitUnauthorized({ status, path, hadSession });
  return true;
}
